import React, { useState, useEffect } from 'react';
import { formatISK } from '@/utils/formatISK';
import { Loader2, X, FileText, Search } from 'lucide-react';
import PortalOverlay from '@/components/PortalOverlay';
import { companyAPI } from '@/services/api';

const ISK = (n) => formatISK(Math.round(Number(n) || 0));

/**
 * InvoiceProjectPicker — modal that lists every project with unbilled work,
 * letting the user pick one to immediately open the InvoiceEditorModal.
 *
 * Used by the dashboard "Búa til reikning" CTA so users don't have to drill
 * into a project first.
 */
const InvoiceProjectPicker = ({ open, onClose, onPick }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const r = await companyAPI.getUnbilledSummary();
        const projects = r.data?.projects || r.data?.uninvoiced_projects || [];
        if (!cancelled) setRows(projects);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Tókst ekki að hlaða verkefni');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const filtered = rows.filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (p.name || '').toLowerCase().includes(q) || (p.client_name || '').toLowerCase().includes(q);
  });

  return (
    <PortalOverlay open={open} onClose={onClose} placement="center" testId="invoice-project-picker">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-screen h-[100dvh] sm:w-[min(560px,95vw)] sm:h-auto sm:max-h-[80dvh] flex flex-col">
        <header className="flex items-center justify-between px-5 py-4 pt-[max(1rem,env(safe-area-inset-top))] border-b border-border">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-accent" />
            <div>
              <h2 className="text-base font-semibold">Búa til reikning</h2>
              <p className="text-xs text-foreground-muted">Veldu verkefni til að reikningsfæra.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-card-hover rounded-lg" data-testid="invoice-picker-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
            <input
              className="input pl-9"
              placeholder="Leita…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="invoice-picker-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-foreground-muted" /></div>
          ) : error ? (
            <div className="m-3 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-foreground-muted text-sm">
              {rows.length === 0 ? 'Engin verkefni með óreikningsfærða vinnu.' : 'Engar niðurstöður.'}
            </div>
          ) : (
            <div className="space-y-1">
              {filtered.map((p) => (
                <button
                  key={p.project_id}
                  onClick={() => onPick?.(p)}
                  className="w-full flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-card-hover text-left transition-colors"
                  data-testid={`invoice-picker-row-${p.project_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <p className="text-[11px] text-foreground-muted truncate">{p.client_name || 'Án viðskiptavinar'}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-mono font-semibold text-amber-400">{ISK(p.unbilled_total || 0)} kr</p>
                    <p className="text-[10px] text-foreground-muted">án VSK</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </PortalOverlay>
  );
};

export default InvoiceProjectPicker;
